import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";

// Landing Page Components
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import VideoSection from "./components/VideoSection";
import AIToolsSection from "./components/AIToolsSection";
import FeaturesSection from "./components/FeaturesSection";
import TestimonialsSection from "./components/TestimonialsSection";
import CTASection from "./components/CTASection";
import PricingSection from "./components/PricingSection";
import FAQSection from "./components/FAQSection";
import Footer from "./components/Footer";

// Pages
import PricingPage from "./components/PricingPage";
import AffiliatePage from "./components/AffiliatePage";
import TemplatesPage from "./components/TemplatesPage";
import WhatsAppPage from "./components/WhatsAppPage";
import IntegrationsPage from "./components/IntegrationsPage";
import UseCasesPage from "./components/UseCasesPage";
import PublicBookingPage from "./components/PublicBookingPage";
import BookingPageCreator from "./components/dashboard/BookingPageCreator";

// All New Pages
import {
  FacebookPage, InstagramPage, TiktokPage, WebsitePage,
  WhatsAppPopupPage, ClickToChatAdsPage, StoryRepliesPage, WhatsAppButtonPage,
  CommentsRepliesPage, QRCodesPage, LinksToMessengersPage, ReelsRepliesPage,
  LeadQualificationPage, ProductRecommendationsPage, BookingAppointmentsPage, PlacingOrderPage,
  AnswerFAQPage, CrossSellUpsellPage, NewProductLaunchesPage, CustomerSupportPage,
  PromotionsPage, RepeatOrdersPage, GatheringFeedbackPage, AppointmentNotificationsPage,
  OrderTrackingPage, ReturnsRefundsPage, ComplaintsPage,
  EducationPage, BeautyPage, RealEstatePage, EcommercePage, HealthcarePage, AestheticBeautyPage, RestaurantsPage,
  StripePage, ZapierPage, GoogleSheetsPage, ShopifyPage, APIPage,
  LiveChatPage, BroadcastsPage as BroadcastsFeaturePage, AISalesManagerPage, KeywordsPage, SegmentationPage,
  SuccessStoriesPage, DocsPage, BlogPage, OnboardingPage
} from "./components/pages";

// Auth Components
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";

// Dashboard Components
import DashboardLayout from "./components/dashboard/DashboardLayout";
import DashboardHome from "./components/dashboard/DashboardHome";
import BotsPage from "./components/dashboard/BotsPage";
import ConversationsPage from "./components/dashboard/ConversationsPage";
import ContactsPage from "./components/dashboard/ContactsPage";
import AutomationsPage from "./components/dashboard/AutomationsPage";
import BroadcastsPage from "./components/dashboard/BroadcastsPage";
import SettingsPage from "./components/dashboard/SettingsPage";
import FlowEditor from "./components/FlowBuilder/FlowEditor";
import CalendarPage from "./components/dashboard/CalendarPage";
import WidgetGenerator from "./components/dashboard/WidgetGenerator";
import BillingPage from "./components/dashboard/BillingPage";
import LeadsPage from "./components/dashboard/LeadsPage";

const LandingPage = () => (
  <>
    <Navbar />
    <HeroSection />
    <VideoSection />
    <AIToolsSection />
    <FeaturesSection />
    <TestimonialsSection />
    <PricingSection />
    <CTASection />
    <FAQSection />
    <Footer />
  </>
);

const AppRouter = () => {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/affiliate" element={<AffiliatePage />} />
      <Route path="/templates" element={<TemplatesPage />} />
      <Route path="/whatsapp" element={<WhatsAppPage />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route path="/use-cases" element={<UseCasesPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/reserve/:slug" element={<PublicBookingPage />} />

      {/* Channels */}
      <Route path="/facebook" element={<FacebookPage />} />
      <Route path="/instagram" element={<InstagramPage />} />
      <Route path="/tiktok" element={<TiktokPage />} />
      <Route path="/website" element={<WebsitePage />} />

      {/* Entry Points */}
      <Route path="/whatsapp-capture-popup" element={<WhatsAppPopupPage />} />
      <Route path="/click-to-chat-ads" element={<ClickToChatAdsPage />} />
      <Route path="/story-replies" element={<StoryRepliesPage />} />
      <Route path="/whatsapp-chat-button" element={<WhatsAppButtonPage />} />
      <Route path="/comments-replies" element={<CommentsRepliesPage />} />
      <Route path="/qr-codes" element={<QRCodesPage />} />
      <Route path="/links-to-messengers" element={<LinksToMessengersPage />} />
      <Route path="/reels-replies" element={<ReelsRepliesPage />} />

      {/* Use Cases */}
      <Route path="/lead-qualification" element={<LeadQualificationPage />} />
      <Route path="/product-recommendations" element={<ProductRecommendationsPage />} />
      <Route path="/booking-appointments" element={<BookingAppointmentsPage />} />
      <Route path="/placing-an-order" element={<PlacingOrderPage />} />
      <Route path="/answer-faq" element={<AnswerFAQPage />} />
      <Route path="/cross-sell-and-upsell" element={<CrossSellUpsellPage />} />
      <Route path="/new-product-launches" element={<NewProductLaunchesPage />} />
      <Route path="/customer-support" element={<CustomerSupportPage />} />
      <Route path="/promotions" element={<PromotionsPage />} />
      <Route path="/repeat-orders" element={<RepeatOrdersPage />} />
      <Route path="/gathering-feedback" element={<GatheringFeedbackPage />} />
      <Route path="/appointment-notifications" element={<AppointmentNotificationsPage />} />
      <Route path="/order-tracking" element={<OrderTrackingPage />} />
      <Route path="/returns-and-refunds" element={<ReturnsRefundsPage />} />
      <Route path="/complaints" element={<ComplaintsPage />} />

      {/* Industries */}
      <Route path="/education" element={<EducationPage />} />
      <Route path="/beauty" element={<BeautyPage />} />
      <Route path="/real-estate" element={<RealEstatePage />} />
      <Route path="/ecommerce" element={<EcommercePage />} />
      <Route path="/healthcare" element={<HealthcarePage />} />
      <Route path="/aesthetic-beauty" element={<AestheticBeautyPage />} />
      <Route path="/restaurants" element={<RestaurantsPage />} />

      {/* Integrations */}
      <Route path="/stripe" element={<StripePage />} />
      <Route path="/zapier" element={<ZapierPage />} />
      <Route path="/google-sheets" element={<GoogleSheetsPage />} />
      <Route path="/integrations/shopify" element={<ShopifyPage />} />
      <Route path="/api" element={<APIPage />} />

      {/* Features */}
      <Route path="/live-chat" element={<LiveChatPage />} />
      <Route path="/broadcasts" element={<BroadcastsFeaturePage />} />
      <Route path="/ai-sales-manager" element={<AISalesManagerPage />} />
      <Route path="/keywords" element={<KeywordsPage />} />
      <Route path="/segmentation" element={<SegmentationPage />} />

      {/* Resources */}
      <Route path="/success-stories" element={<SuccessStoriesPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/blog" element={<BlogPage />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardHome />} />
        <Route path="bots" element={<BotsPage />} />
        <Route path="templates" element={<TemplatesPage embedded={true} />} />
        <Route path="bots/:botId/flow" element={<FlowEditor />} />
        <Route path="conversations" element={<ConversationsPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="automations" element={<AutomationsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="widget" element={<WidgetGenerator />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="website" element={<BookingPageCreator />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="broadcasts" element={<BroadcastsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
